import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API } from "../context/AppContext";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

function Stars({ value, onChange, size = 18 }) {
  return (
    <div className="inline-flex items-center gap-1">
      {[1,2,3,4,5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange?.(n)}
          disabled={!onChange}
          data-testid={onChange ? `review-star-${n}` : undefined}
          className={`transition-colors ${onChange ? "cursor-pointer" : "cursor-default"} ${n <= value ? "text-[color:var(--bl-gold)]" : "text-white/20"} hover:opacity-90`}
          aria-label={`${n} star`}
        >
          <Star size={size} fill={n <= value ? "currentColor" : "none"} strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const sectionRef = useRef(null);
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews`).then(r => setData(r.data)).catch(() => {});
    if (window.location.hash === "#reviews") {
      setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, { name, rating, title: title || null, comment });
      toast.success("Thanks — your review is pending moderation");
      setName(""); setTitle(""); setComment(""); setRating(5);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally { setSending(false); }
  };

  return (
    <section id="reviews" ref={sectionRef} className="relative py-24 lg:py-32 border-t border-white/5">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="bl-eyebrow text-[color:var(--bl-gold)]">/ Reviews</div>
            <h2 className="bl-heading text-white text-4xl md:text-5xl lg:text-6xl mt-4">What buyers say.</h2>
          </div>
          {data.count > 0 && (
            <div className="text-right" data-testid="reviews-summary">
              <div className="flex items-center justify-end gap-3">
                <Stars value={Math.round(data.average)} size={22} />
                <span className="font-display text-white text-3xl">{data.average.toFixed(1)}</span>
              </div>
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/40 mt-2">{data.count} verified review{data.count !== 1 ? "s" : ""}</div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Reviews list */}
          <div>
            {data.reviews.length === 0 ? (
              <div className="border border-white/8 p-8 text-white/50" data-testid="reviews-empty">
                No reviews yet. Be the first to share your experience — every submission is moderated before it appears here.
              </div>
            ) : (
              <div className="space-y-4">
                {data.reviews.map(r => (
                  <div key={r.id} data-testid={`review-${r.id}`} className="border border-white/8 p-6 bg-white/2">
                    <div className="flex items-center justify-between mb-3">
                      <Stars value={r.rating} size={15} />
                      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">
                        {new Date(r.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {r.title && <div className="font-display uppercase text-white tracking-tight text-lg mb-2">{r.title}</div>}
                    <p className="text-white/70 leading-relaxed text-sm">{r.comment}</p>
                    <div className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-white/50">— {r.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submission form */}
          <form onSubmit={submit} className="bl-glass border border-white/8 p-6 lg:p-8 space-y-4" data-testid="review-form">
            <div className="bl-eyebrow text-[color:var(--bl-blue)]">Leave a Review</div>
            <div className="font-display text-white uppercase tracking-tight text-xl">Share your experience</div>
            <p className="text-white/50 text-sm">All reviews are read and moderated before they appear. No fake reviews, ever.</p>
            <div>
              <label className="bl-eyebrow block mb-2">Your Name</label>
              <input data-testid="review-name" required value={name} onChange={e=>setName(e.target.value)} maxLength={60}
                className="w-full bg-transparent border border-white/15 focus:border-[color:var(--bl-blue)] outline-none px-3 py-3 text-white font-mono text-sm" />
            </div>
            <div>
              <label className="bl-eyebrow block mb-2">Rating</label>
              <Stars value={rating} onChange={setRating} />
            </div>
            <div>
              <label className="bl-eyebrow block mb-2">Title <span className="text-white/30 normal-case">(optional)</span></label>
              <input data-testid="review-title" value={title} onChange={e=>setTitle(e.target.value)} maxLength={80}
                className="w-full bg-transparent border border-white/15 focus:border-[color:var(--bl-blue)] outline-none px-3 py-3 text-white font-mono text-sm" />
            </div>
            <div>
              <label className="bl-eyebrow block mb-2">Comment</label>
              <textarea data-testid="review-comment" required minLength={4} maxLength={1000} rows={4}
                value={comment} onChange={e=>setComment(e.target.value)}
                className="w-full bg-transparent border border-white/15 focus:border-[color:var(--bl-blue)] outline-none px-3 py-3 text-white font-mono text-sm" />
            </div>
            <button data-testid="review-submit" disabled={sending} className="w-full bl-btn primary disabled:opacity-60">
              {sending ? <><Loader2 size={14} className="animate-spin" /> Submitting</> : "Submit Review"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
